import { Currency, Holding, LiquidityEvent } from '../types'
import { convertCurrency } from './currency'

// ─── Compute weighted rates from per-holding settings ───────────────────────

export interface WeightedRates {
  liquidRate: number // weighted avg return for liquid holdings
  illiquidRate: number // weighted avg return for illiquid holdings
  weightedCGT: number // weighted avg CGT across all holdings
  blendedRate: number // weighted avg return across ALL holdings
}

/**
 * Compute weighted average return rates and CGT from individual holdings.
 * For RE holdings, uses appreciationRate as the return rate.
 * Holdings with no expectedReturn default to 0%.
 */
export function computeWeightedRates(
  holdings: Holding[],
  baseCurrency: Currency,
): WeightedRates {
  let liquidWeightedReturn = 0
  let liquidTotal = 0
  let illiquidWeightedReturn = 0
  let illiquidTotal = 0
  let cgtWeightedSum = 0
  let cgtTotal = 0

  for (const h of holdings) {
    if (!(h.isActive ?? true)) continue
    const value = convertCurrency(h.amount, h.currency, baseCurrency)
    if (value <= 0) continue

    // Determine the effective return rate for this holding
    const rate = h.isRealEstate
      ? (h.appreciationRate ?? h.expectedReturn ?? 0)
      : (h.expectedReturn ?? 0)

    const cgt = h.capitalGainsTaxRate ?? 0

    if (h.type === 'Liquid') {
      liquidWeightedReturn += value * rate
      liquidTotal += value
    } else {
      illiquidWeightedReturn += value * rate
      illiquidTotal += value
    }

    cgtWeightedSum += value * cgt
    cgtTotal += value
  }

  const liquidRate = liquidTotal > 0 ? liquidWeightedReturn / liquidTotal : 0
  const illiquidRate =
    illiquidTotal > 0 ? illiquidWeightedReturn / illiquidTotal : 0
  const weightedCGT = cgtTotal > 0 ? cgtWeightedSum / cgtTotal : 0
  const totalValue = liquidTotal + illiquidTotal
  const blendedRate =
    totalValue > 0
      ? (liquidWeightedReturn + illiquidWeightedReturn) / totalValue
      : 0

  return { liquidRate, illiquidRate, weightedCGT, blendedRate }
}

// ─── Illiquid bucket for delayed drawdown availability ──────────────────────

export interface IlliquidBucket {
  name: string // holding name for display
  amount: number // value in base currency
  returnRate: number // annual % return for this bucket
  availableYear: number // years from now when available for drawdown
  yieldsAfterDrawdown: boolean // whether it keeps earning after being drawn down
}

/**
 * Build illiquid buckets from holdings for use in projection.
 */
export function buildIlliquidBuckets(
  holdings: Holding[],
  baseCurrency: Currency,
): IlliquidBucket[] {
  const buckets: IlliquidBucket[] = []
  for (const h of holdings) {
    if (!(h.isActive ?? true)) continue
    if (h.type !== 'Illiquid') continue
    const value = convertCurrency(h.amount, h.currency, baseCurrency)
    if (value <= 0) continue
    const rate = h.isRealEstate
      ? (h.appreciationRate ?? h.expectedReturn ?? 0)
      : (h.expectedReturn ?? 0)

    if (h.isRealEstate && h.realEstateStrategy) {
      // New RE strategy model
      if (h.realEstateStrategy === 'hold') {
        // Hold = never enters drawdown pool, but still appreciates for net worth
        buckets.push({
          name: h.name,
          amount: value,
          returnRate: rate,
          availableYear: 999, // effectively never available for drawdown
          yieldsAfterDrawdown: true, // keeps appreciating (for net worth tracking)
        })
      } else {
        // Sell = liquidated at planned sale year, proceeds enter drawdown pool
        buckets.push({
          name: h.name,
          amount: value,
          returnRate: rate,
          availableYear: h.plannedSaleYear ?? 0,
          yieldsAfterDrawdown: false, // once sold, no more appreciation
        })
      }
    } else {
      // Non-RE illiquid assets or legacy RE without strategy — use existing fields
      buckets.push({
        name: h.name,
        amount: value,
        returnRate: rate,
        availableYear: h.drawdownAvailableYear ?? 0,
        yieldsAfterDrawdown: h.yieldsAfterDrawdown ?? true,
      })
    }
  }
  return buckets
}

export interface ProjectionParams {
  totalLiquid: number
  totalIlliquid: number
  liquidReturnRate: number // annual %
  illiquidReturnRate: number // annual %
  drawdownReturnRate: number // annual % during retirement
  monthlyContribution: number
  retirementYear: number
  monthlyDrawdown: number
  totalYears: number
  events?: LiquidityEvent[]
  baseCurrency?: Currency
  annualInflation?: number
  capitalGainsTaxRate?: number // % tax on returns (e.g. 20 = 20%)
  illiquidBuckets?: IlliquidBucket[] // per-holding illiquid data for delayed drawdown
}

export interface YearData {
  year: number
  label: string
  liquid: number
  illiquid: number
  total: number
  contributed: number
  withdrawn: number
  phase: 'accumulation' | 'drawdown'
  depleted: boolean
  monthlyDrawdown?: number // inflation-adjusted monthly drawdown for this year
}

/**
 * Compute the net cash flow from liquidity events for a given year and month.
 * All amounts converted to baseCurrency.
 */
function getEventCashFlow(
  events: LiquidityEvent[],
  year: number,
  month: number, // 0-11
  baseCurrency: Currency,
): number {
  let cashFlow = 0
  for (const event of events) {
    const endYear = event.endYear ?? Infinity

    if (event.type === 'one-time') {
      // Apply at month 0 of startYear
      if (year === event.startYear && month === 0) {
        cashFlow += convertCurrency(event.amount, event.currency, baseCurrency)
      }
    } else if (event.type === 'monthly') {
      // Apply every month within [startYear, endYear]
      if (year >= event.startYear && year <= endYear) {
        // If start year, only from month 0; if end year, all months (endYear is inclusive)
        cashFlow += convertCurrency(event.amount, event.currency, baseCurrency)
      }
    } else if (event.type === 'yearly') {
      // Apply at month 0 of each year within [startYear, endYear]
      if (year >= event.startYear && year <= endYear && month === 0) {
        cashFlow += convertCurrency(event.amount, event.currency, baseCurrency)
      }
    }
  }
  return cashFlow
}

/**
 * Get the inflation-adjusted monthly drawdown for a given year of retirement.
 * Year 0 of retirement = base amount, year 1 = base * (1 + rate), etc.
 */
function getInflatedDrawdown(
  baseMonthly: number,
  yearsIntoRetirement: number,
  annualInflation: number,
): number {
  if (annualInflation <= 0 || yearsIntoRetirement <= 0) return baseMonthly
  return baseMonthly * Math.pow(1 + annualInflation / 100, yearsIntoRetirement)
}

/**
 * Unified month-by-month projection engine.
 *
 * Phase 1 — Accumulation (year 0 to retirementYear):
 *   - Monthly contributions added to liquid holdings
 *   - Liquid grows at liquidReturnRate, illiquid at illiquidReturnRate
 *   - Liquidity events applied to liquid holdings
 *   - No withdrawals
 *
 * Phase 2 — Drawdown (retirementYear onwards):
 *   - No more contributions
 *   - Illiquid assets that are available merge into the drawdown pool
 *   - Illiquid assets not yet available stay separate and keep growing
 *   - When delayed illiquid becomes available, it joins the pool
 *   - Assets with yieldsAfterDrawdown=false join a non-yielding sub-pool
 *   - Monthly drawdown withdrawn from non-yielding pool first, then yielding pool
 *   - Liquidity events still applied
 *   - Yielding pool grows at drawdownReturnRate
 */
export function runProjection(params: ProjectionParams): YearData[] {
  const {
    totalLiquid,
    totalIlliquid,
    liquidReturnRate,
    illiquidReturnRate,
    drawdownReturnRate,
    monthlyContribution,
    retirementYear,
    monthlyDrawdown,
    totalYears,
    events = [],
    baseCurrency = 'USD' as Currency,
    annualInflation = 0,
    capitalGainsTaxRate = 0,
    illiquidBuckets,
  } = params

  const data: YearData[] = []
  let liquid = totalLiquid
  let illiquid = totalIlliquid
  let yieldingPool = 0 // drawdown pool that earns returns
  let nonYieldingPool = 0 // drawdown pool that doesn't earn returns
  let totalContributed = totalLiquid + totalIlliquid
  let totalWithdrawn = 0
  let depleted = false
  let inDrawdown = false

  const liquidMonthlyRate = liquidReturnRate / 100 / 12
  const illiquidMonthlyRate = illiquidReturnRate / 100 / 12
  const drawdownMonthlyRate = drawdownReturnRate / 100 / 12
  const taxMultiplier = 1 - capitalGainsTaxRate / 100

  // Track delayed illiquid buckets — grow them separately during drawdown
  // Each bucket tracks its current grown value
  const hasBuckets = illiquidBuckets && illiquidBuckets.length > 0
  let pendingBuckets: {
    amount: number
    monthlyRate: number
    availableYear: number
    yieldsAfterDrawdown: boolean
  }[] = []

  if (hasBuckets) {
    pendingBuckets = illiquidBuckets.map((b) => ({
      amount: b.amount,
      monthlyRate: b.returnRate / 100 / 12,
      availableYear: b.availableYear,
      yieldsAfterDrawdown: b.yieldsAfterDrawdown,
    }))
  }

  for (let year = 0; year <= totalYears; year++) {
    if (year === 0) {
      data.push({
        year,
        label: `Yr ${year}`,
        liquid,
        illiquid,
        total: liquid + illiquid,
        contributed: totalContributed,
        withdrawn: 0,
        phase: 'accumulation',
        depleted: false,
      })
      continue
    }

    const yearsIntoRetirement =
      inDrawdown || year > retirementYear ? year - retirementYear : 0
    const currentMonthlyDrawdown = getInflatedDrawdown(
      monthlyDrawdown,
      yearsIntoRetirement,
      annualInflation,
    )

    for (let month = 0; month < 12; month++) {
      const eventCash = getEventCashFlow(events, year, month, baseCurrency)

      if (!inDrawdown && year <= retirementYear) {
        // ACCUMULATION PHASE — unchanged
        liquid += monthlyContribution
        totalContributed += monthlyContribution
        liquid += eventCash
        const liquidInterest = liquid * liquidMonthlyRate
        liquid += liquidInterest * taxMultiplier
        const illiquidInterest = illiquid * illiquidMonthlyRate
        illiquid += illiquidInterest * taxMultiplier

        // Also grow pending buckets during accumulation (they're part of illiquid total already,
        // so we don't double-count — the bucket amounts track individual growth for drawdown timing)
        if (hasBuckets) {
          for (const b of pendingBuckets) {
            const interest = b.amount * b.monthlyRate
            b.amount += interest * taxMultiplier
          }
        }
      } else {
        if (!inDrawdown) {
          // TRANSITION TO DRAWDOWN
          inDrawdown = true

          if (hasBuckets) {
            // Use bucket-level data for smarter merging
            // Liquid always merges into yielding pool
            yieldingPool = liquid

            // Merge available illiquid buckets, keep others pending
            const stillPending: typeof pendingBuckets = []
            for (const b of pendingBuckets) {
              if (b.availableYear <= retirementYear) {
                if (b.yieldsAfterDrawdown) {
                  yieldingPool += b.amount
                } else {
                  nonYieldingPool += b.amount
                }
              } else {
                stillPending.push(b)
              }
            }
            pendingBuckets = stillPending
          } else {
            // No buckets — original behavior: merge everything
            yieldingPool = liquid + illiquid
          }

          liquid = 0
          illiquid = 0
        }

        // Check if any pending buckets become available this year (at month 0)
        if (hasBuckets && month === 0 && pendingBuckets.length > 0) {
          const stillPending: typeof pendingBuckets = []
          for (const b of pendingBuckets) {
            if (b.availableYear <= year) {
              if (b.yieldsAfterDrawdown) {
                yieldingPool += b.amount
              } else {
                nonYieldingPool += b.amount
              }
            } else {
              stillPending.push(b)
            }
          }
          pendingBuckets = stillPending
        }

        // Grow pending buckets that are still waiting
        if (hasBuckets) {
          for (const b of pendingBuckets) {
            const interest = b.amount * b.monthlyRate
            b.amount += interest * taxMultiplier
          }
        }

        if (!depleted) {
          const totalPool = yieldingPool + nonYieldingPool
          const pendingTotal = pendingBuckets.reduce((s, b) => s + b.amount, 0)

          // Apply events to yielding pool
          yieldingPool += eventCash

          // Withdraw: take from non-yielding pool first, then yielding
          let remaining = currentMonthlyDrawdown
          if (nonYieldingPool > 0) {
            const fromNonYielding = Math.min(remaining, nonYieldingPool)
            nonYieldingPool -= fromNonYielding
            remaining -= fromNonYielding
          }
          if (remaining > 0) {
            yieldingPool -= remaining
          }
          totalWithdrawn += currentMonthlyDrawdown

          if (yieldingPool + nonYieldingPool + pendingTotal <= 0) {
            yieldingPool = 0
            nonYieldingPool = 0
            depleted = true
          } else {
            // Only yielding pool earns returns
            if (yieldingPool > 0) {
              const interest = yieldingPool * drawdownMonthlyRate
              yieldingPool += interest * taxMultiplier
            }
          }
        }
      }
    }

    const pendingTotal = hasBuckets
      ? pendingBuckets.reduce((s, b) => s + b.amount, 0)
      : 0
    const currentTotal = inDrawdown
      ? yieldingPool + nonYieldingPool + pendingTotal
      : liquid + illiquid

    data.push({
      year,
      label: `Yr ${year}`,
      liquid: inDrawdown ? 0 : Math.round(liquid),
      illiquid: inDrawdown ? 0 : Math.round(illiquid),
      total: Math.round(currentTotal),
      contributed: Math.round(totalContributed),
      withdrawn: Math.round(totalWithdrawn),
      phase: inDrawdown ? 'drawdown' : 'accumulation',
      depleted,
      monthlyDrawdown: inDrawdown
        ? Math.round(currentMonthlyDrawdown)
        : undefined,
    })
  }

  return data
}

/**
 * Get portfolio value at a specific year during accumulation.
 * Runs a quick accumulation-only simulation including liquidity events.
 */
export function getPortfolioAtYear(
  totalLiquid: number,
  totalIlliquid: number,
  liquidReturnRate: number,
  illiquidReturnRate: number,
  monthlyContribution: number,
  years: number,
  events: LiquidityEvent[] = [],
  baseCurrency: Currency = 'USD' as Currency,
  capitalGainsTaxRate: number = 0,
): number {
  let liquid = totalLiquid
  let illiquid = totalIlliquid
  const lRate = liquidReturnRate / 100 / 12
  const iRate = illiquidReturnRate / 100 / 12
  const taxMul = 1 - capitalGainsTaxRate / 100
  for (let year = 1; year <= years; year++) {
    for (let month = 0; month < 12; month++) {
      liquid += monthlyContribution
      const eventCash = getEventCashFlow(events, year, month, baseCurrency)
      liquid += eventCash
      liquid += liquid * lRate * taxMul
      illiquid += illiquid * iRate * taxMul
    }
  }
  return liquid + illiquid
}

/**
 * Find the earliest retirement year where the portfolio can sustain
 * the desired monthly drawdown for the remaining years of the horizon.
 * Now supports illiquid buckets for delayed availability.
 */
export function findEarliestRetirement(
  totalLiquid: number,
  totalIlliquid: number,
  liquidReturnRate: number,
  illiquidReturnRate: number,
  drawdownReturnRate: number,
  monthlyContribution: number,
  monthlyDrawdown: number,
  maxHorizon: number,
  withdrawalStrategy: 'drawdown' | 'interest-only' = 'drawdown',
  events: LiquidityEvent[] = [],
  baseCurrency: Currency = 'USD' as Currency,
  annualInflation: number = 0,
  capitalGainsTaxRate: number = 0,
  illiquidBuckets?: IlliquidBucket[],
): { year: number; portfolioAtRetirement: number } | null {
  const drawdownMonthlyRate = drawdownReturnRate / 100 / 12
  const taxMul = 1 - capitalGainsTaxRate / 100
  const interestOnlyHorizon = 60
  const hasBuckets = illiquidBuckets && illiquidBuckets.length > 0

  for (let retireYear = 0; retireYear <= maxHorizon; retireYear++) {
    const portfolio = getPortfolioAtYear(
      totalLiquid,
      totalIlliquid,
      liquidReturnRate,
      illiquidReturnRate,
      monthlyContribution,
      retireYear,
      events,
      baseCurrency,
      capitalGainsTaxRate,
    )

    // If we have buckets, simulate with yielding/non-yielding split
    if (hasBuckets) {
      // Grow buckets to retirement year
      const grownBuckets = illiquidBuckets.map((b) => {
        let amount = b.amount
        const monthlyRate = b.returnRate / 100 / 12
        for (let y = 1; y <= retireYear; y++) {
          for (let m = 0; m < 12; m++) {
            amount += amount * monthlyRate * taxMul
          }
        }
        return { ...b, amount }
      })

      // Split into available and pending at retirement
      const liquidAtRetirement =
        portfolio - grownBuckets.reduce((s, b) => s + b.amount, 0)
      let yieldPool = Math.max(0, liquidAtRetirement)
      let nonYieldPool = 0
      const pending = grownBuckets.filter((b) => b.availableYear > retireYear)
      const available = grownBuckets.filter(
        (b) => b.availableYear <= retireYear,
      )

      for (const b of available) {
        if (b.yieldsAfterDrawdown) {
          yieldPool += b.amount
        } else {
          nonYieldPool += b.amount
        }
      }

      // Simulate drawdown
      const simHorizon =
        withdrawalStrategy === 'interest-only'
          ? interestOnlyHorizon
          : maxHorizon - retireYear
      if (simHorizon <= 0) continue

      let survives = true
      // Clone pending for simulation
      const simPending = pending.map((b) => ({
        amount: b.amount,
        monthlyRate: b.returnRate / 100 / 12,
        availableYear: b.availableYear,
        yieldsAfterDrawdown: b.yieldsAfterDrawdown,
      }))

      for (let m = 0; m < simHorizon * 12; m++) {
        const simYear = retireYear + Math.floor(m / 12) + 1
        const simMonth = m % 12
        const yearsIntoRetirement = Math.floor(m / 12)
        const inflatedDrawdown = getInflatedDrawdown(
          monthlyDrawdown,
          yearsIntoRetirement,
          annualInflation,
        )

        // Check for newly available buckets at start of each year
        if (simMonth === 0 && simPending.length > 0) {
          const still: typeof simPending = []
          for (const b of simPending) {
            if (b.availableYear <= simYear) {
              if (b.yieldsAfterDrawdown) {
                yieldPool += b.amount
              } else {
                nonYieldPool += b.amount
              }
            } else {
              still.push(b)
            }
          }
          simPending.length = 0
          simPending.push(...still)
        }

        // Grow pending
        for (const b of simPending) {
          b.amount += b.amount * b.monthlyRate * taxMul
        }

        const eventCash = getEventCashFlow(
          events,
          simYear,
          simMonth,
          baseCurrency,
        )
        yieldPool += eventCash

        // Withdraw from non-yielding first
        let remaining = inflatedDrawdown
        if (nonYieldPool > 0) {
          const take = Math.min(remaining, nonYieldPool)
          nonYieldPool -= take
          remaining -= take
        }
        yieldPool -= remaining

        const pendingVal = simPending.reduce((s, b) => s + b.amount, 0)
        if (yieldPool + nonYieldPool + pendingVal <= 0) {
          survives = false
          break
        }

        if (yieldPool > 0) {
          yieldPool += yieldPool * drawdownMonthlyRate * taxMul
        }
      }

      if (survives) {
        return { year: retireYear, portfolioAtRetirement: portfolio }
      }
      continue
    }

    // Original logic (no buckets)
    if (withdrawalStrategy === 'interest-only') {
      let balance = portfolio
      let survives = true

      for (let m = 0; m < interestOnlyHorizon * 12; m++) {
        const simYear = retireYear + Math.floor(m / 12) + 1
        const simMonth = m % 12
        const yearsIntoRetirement = Math.floor(m / 12)
        const inflatedDrawdown = getInflatedDrawdown(
          monthlyDrawdown,
          yearsIntoRetirement,
          annualInflation,
        )
        const eventCash = getEventCashFlow(
          events,
          simYear,
          simMonth,
          baseCurrency,
        )
        balance += eventCash
        balance -= inflatedDrawdown
        if (balance <= 0) {
          survives = false
          break
        }
        const interest = balance * drawdownMonthlyRate
        balance += interest * taxMul
      }

      if (survives) {
        return { year: retireYear, portfolioAtRetirement: portfolio }
      }
      continue
    }

    const remainingYears = maxHorizon - retireYear
    if (remainingYears <= 0) continue

    let balance = portfolio
    let survives = true
    for (let m = 0; m < remainingYears * 12; m++) {
      const drawdownYear = retireYear + Math.floor(m / 12) + 1
      const drawdownMonth = m % 12
      const yearsIntoRetirement = Math.floor(m / 12)
      const inflatedDrawdown = getInflatedDrawdown(
        monthlyDrawdown,
        yearsIntoRetirement,
        annualInflation,
      )
      const eventCash = getEventCashFlow(
        events,
        drawdownYear,
        drawdownMonth,
        baseCurrency,
      )
      balance += eventCash
      balance -= inflatedDrawdown
      if (balance <= 0) {
        survives = false
        break
      }
      const interest = balance * drawdownMonthlyRate
      balance += interest * taxMul
    }

    if (survives) {
      return { year: retireYear, portfolioAtRetirement: portfolio }
    }
  }

  return null
}

/**
 * Find the maximum sustainable monthly drawdown for a given retirement year
 * and drawdown duration, using binary search.
 */
export function findMaxDrawdown(
  totalLiquid: number,
  totalIlliquid: number,
  liquidReturnRate: number,
  illiquidReturnRate: number,
  drawdownReturnRate: number,
  monthlyContribution: number,
  retirementYear: number,
  drawdownDuration: number,
  events: LiquidityEvent[] = [],
  baseCurrency: Currency = 'USD' as Currency,
  annualInflation: number = 0,
  capitalGainsTaxRate: number = 0,
): { maxDrawdown: number; portfolioAtRetirement: number } {
  const portfolio = getPortfolioAtYear(
    totalLiquid,
    totalIlliquid,
    liquidReturnRate,
    illiquidReturnRate,
    monthlyContribution,
    retirementYear,
    events,
    baseCurrency,
    capitalGainsTaxRate,
  )

  const drawdownMonthlyRate = drawdownReturnRate / 100 / 12
  const taxMul = 1 - capitalGainsTaxRate / 100
  let low = 0,
    high = portfolio / 12,
    maxDrawdown = 0

  for (let i = 0; i < 50; i++) {
    const mid = (low + high) / 2
    let balance = portfolio
    let survives = true

    for (let m = 0; m < drawdownDuration * 12; m++) {
      const drawdownYear = retirementYear + Math.floor(m / 12) + 1
      const drawdownMonth = m % 12
      const yearsIntoRetirement = Math.floor(m / 12)
      const inflatedDrawdown = getInflatedDrawdown(
        mid,
        yearsIntoRetirement,
        annualInflation,
      )
      const eventCash = getEventCashFlow(
        events,
        drawdownYear,
        drawdownMonth,
        baseCurrency,
      )
      balance += eventCash
      balance -= inflatedDrawdown
      if (balance <= 0) {
        survives = false
        break
      }
      const interest = balance * drawdownMonthlyRate
      balance += interest * taxMul
    }

    if (survives) {
      maxDrawdown = mid
      low = mid
    } else {
      high = mid
    }
  }

  return { maxDrawdown, portfolioAtRetirement: portfolio }
}

export interface DrawdownYearBreakdown {
  year: number
  totalWithdrawal: number
  fromInterest: number
  fromEvents: number
  fromPrincipal: number
  balanceStart: number
  balanceEnd: number
  monthlyDrawdown: number // inflation-adjusted monthly amount for this year
}

/**
 * Simulate the drawdown phase year-by-year, tracking where withdrawal
 * funds come from: interest earned, liquidity events, or principal.
 */
export function simulateDrawdownBreakdown(
  totalLiquid: number,
  totalIlliquid: number,
  liquidReturnRate: number,
  illiquidReturnRate: number,
  drawdownReturnRate: number,
  monthlyContribution: number,
  monthlyDrawdown: number,
  retirementYear: number,
  maxYears: number,
  withdrawalStrategy: 'drawdown' | 'interest-only',
  events: LiquidityEvent[] = [],
  baseCurrency: Currency = 'USD' as Currency,
  annualInflation: number = 0,
  capitalGainsTaxRate: number = 0,
): DrawdownYearBreakdown[] {
  if (retirementYear < 0 || monthlyDrawdown <= 0) return []

  const portfolioAtRetirement = getPortfolioAtYear(
    totalLiquid,
    totalIlliquid,
    liquidReturnRate,
    illiquidReturnRate,
    monthlyContribution,
    retirementYear,
    events,
    baseCurrency,
    capitalGainsTaxRate,
  )

  const drawdownMonthlyRate = drawdownReturnRate / 100 / 12
  const taxMul = 1 - capitalGainsTaxRate / 100
  let balance = portfolioAtRetirement
  const results: DrawdownYearBreakdown[] = []

  const endYear = Math.min(maxYears, retirementYear + 60)

  for (let year = retirementYear + 1; year <= endYear; year++) {
    if (balance <= 0) break

    const yearsIntoRetirement = year - retirementYear - 1
    const currentMonthlyDrawdown = getInflatedDrawdown(
      monthlyDrawdown,
      yearsIntoRetirement,
      annualInflation,
    )

    const balanceStart = balance
    let yearInterest = 0
    let yearEvents = 0
    let yearWithdrawal = 0

    for (let month = 0; month < 12; month++) {
      if (balance <= 0) break

      const eventCash = getEventCashFlow(events, year, month, baseCurrency)
      if (eventCash > 0) yearEvents += eventCash
      balance += eventCash

      const withdrawal = Math.min(currentMonthlyDrawdown, Math.max(balance, 0))
      balance -= withdrawal
      yearWithdrawal += withdrawal

      if (balance > 0) {
        const grossInterest = balance * drawdownMonthlyRate
        const netInterest = grossInterest * taxMul
        yearInterest += netInterest
        balance += netInterest
      }
    }

    // Attribution: events and interest both contributed to funding withdrawals.
    // Attribute proportionally when combined sources exceed the withdrawal.
    const totalSources = yearInterest + yearEvents
    let fromInterest: number
    let fromEvents: number
    let fromPrincipal: number

    if (totalSources >= yearWithdrawal) {
      // Interest + events fully cover the withdrawal — split proportionally
      if (totalSources > 0) {
        fromInterest = (yearInterest / totalSources) * yearWithdrawal
        fromEvents = (yearEvents / totalSources) * yearWithdrawal
      } else {
        fromInterest = 0
        fromEvents = 0
      }
      fromPrincipal = 0
    } else {
      // Not enough from interest + events — remainder comes from principal
      fromInterest = yearInterest
      fromEvents = yearEvents
      fromPrincipal = yearWithdrawal - fromInterest - fromEvents
    }

    results.push({
      year,
      totalWithdrawal: Math.round(yearWithdrawal),
      fromInterest: Math.round(fromInterest),
      fromEvents: Math.round(fromEvents),
      fromPrincipal: Math.round(fromPrincipal),
      balanceStart: Math.round(balanceStart),
      balanceEnd: Math.round(Math.max(balance, 0)),
      monthlyDrawdown: Math.round(currentMonthlyDrawdown),
    })

    if (balance <= 0) break
  }

  return results
}
